import { useCallback, useMemo, useRef } from 'react';
import { useLoaderData, useLocation } from 'react-router';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import DetailModules from '~/modules/page-builder/component/detail/detail-modules/DetailModules';
import { ProductTaggingContextProvider } from '~/modules/product-tagging/context';
import { ModulesContextProvider } from '~/modules/page-builder/context/modules-context';
import { PaintPickerContextProvider } from '@modules/paint-picker-new/context/PaintPickerContext';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { useProjectPreviewRouteData } from '@modules/project/hook/use-project-preview-route-data';
import getModuleWithAds from '@modules/project/util/get-module-with-ads';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    type IslandMode,
    useIslandSection,
    useScrolledPast,
} from '~/ui-rocco/pages/detail-shared';
import { useProjectBookMeeting } from '../hooks/use-project-book-meeting';
import { useBookMeetingAccordion } from '../hooks/use-book-meeting-accordion';
import {
    BOOK_A_MEETING,
    useProjectEnquiryModal,
} from '../hooks/use-project-enquiry-modal';

/**
 * P7 — the project image gallery. Reskins the existing page-builder renderer
 * (`DetailModules`) on the dark editorial background: the same module layouts,
 * in-image product-tag pins, video modules and ad injection as the legacy page,
 * but without the "Project Gallery" heading so the images flow edge-to-edge.
 */
export const ProjectGallerySection = () => {
    // Preview-merged project so BM template-preview drafts render the edited
    // modules/banner; the rest stays on the published loader data.
    const project = useProjectPreviewRouteData();
    const { memberPermissions, memberRecentlyTagged, moduleMeta, ads } =
        useLoaderData<typeof loader>();
    const { desktop: isDesktop, mobile: isMobile } = useAppDisplayModeContext();
    const location = useLocation();
    const modulesContainerRef = useRef<HTMLDivElement>(null);

    const hideAd = location.state?.hideAd;
    const adsMemoized = useLocationMemo(
        hideAd || typeof document === 'undefined' ? [] : ads,
    );
    const modulesWithAds = useMemo(
        () => getModuleWithAds(project.Modules, adsMemoized),
        [project.Modules, adsMemoized],
    );

    // Magic Island: while the reader scrolls through the page-builder blocks
    // (past 30% of the page), hand the island a "Book a Meeting" CTA — opening
    // the inline booking row when a session exists, else the enquiry modal (the
    // same fallback as the summary CTA and the (03) accordion row).
    const { canBookDirect } = useProjectBookMeeting();
    const { focusBookMeeting } = useBookMeetingAccordion();
    const { openEnquiryModal } = useProjectEnquiryModal();
    const scrolledIntoGallery = useScrolledPast(0.3);
    const bookMeetingMode = useMemo<IslandMode>(
        () => ({
            kind: 'cta',
            label: 'Like this Project? Book a meeting',
            onClick: canBookDirect
                ? focusBookMeeting
                : () =>
                      openEnquiryModal({
                          title: BOOK_A_MEETING,
                          ctaLabel: BOOK_A_MEETING,
                      }),
        }),
        [canBookDirect, focusBookMeeting, openEnquiryModal],
    );
    const islandRef = useIslandSection<HTMLElement>(bookMeetingMode, {
        enabled: scrolledIntoGallery,
    });
    // The section root already carries the PaintPicker container ref; fan the
    // node out to both that ref and the island's measured-element callback.
    const setSectionRef = useCallback(
        (node: HTMLElement | null) => {
            // modulesContainerRef is a RefObject (readonly `current`); the
            // section is its only owner, so write through a mutable view.
            (
                modulesContainerRef as { current: HTMLDivElement | null }
            ).current = node as HTMLDivElement | null;
            islandRef(node);
        },
        [islandRef],
    );

    if (!project.Modules?.length) return null;

    const { canCreateTag, canDeleteTag } = memberPermissions;

    return (
        <PaintPickerContextProvider
            containerRef={modulesContainerRef}
            projectId={project.ID}
            projectLink={project.Link}
            bannerUrl={project.Banner?.imageUrl}
            isDesktop={isDesktop}
            isEnabled={false}
        >
            <ProductTaggingContextProvider
                canCreateTag={isDesktop && canCreateTag}
                canDeleteTag={canDeleteTag}
                recentlyTaggedProducts={memberRecentlyTagged.products}
                recentlyTaggedProfessionals={memberRecentlyTagged.professionals}
            >
                <ModulesContextProvider meta={moduleMeta}>
                    <section
                        ref={setSectionRef}
                        id="project-gallery"
                        // All module text sits on the dark editorial background,
                        // so force it white — headings, paragraphs and quotes
                        // render their rich text in divs/headings (with their own
                        // dark text-[#161616]), and the HTML module is arbitrary
                        // CMS markup, so covering p alone left those dark. Cover
                        // the text-bearing elements (links stay the muted grey;
                        // spans inherit, so link labels keep that grey too). The
                        // in-image product-tag modal keeps its own dark-on-light
                        // card text — the more specific `.product-tagging-tag *`
                        // rule wins. Paragraphs/captions/links also take the body
                        // font; captions render the muted #cbcbcb from the Figma
                        // spec; headings keep their display font. The quote
                        // module's top/bottom rules use the light-theme body
                        // colour, so re-tint them to the section's white/20
                        // hairline. Scoped to this section so the legacy/light
                        // pages are untouched.
                        className="-scroll-mt-32 bg-transparent md:px-[3rem] [&_.editor-quote]:border-white/20! [&_.product-tagging-tag_*]:text-[#1f1f21]! [&_.ui-caption]:font-sans! [&_.ui-caption]:font-normal! [&_.ui-caption]:text-[#cbcbcb]! [&_a]:font-sans! [&_a]:text-[#a3a3a3]! [&_blockquote]:text-white! [&_div]:text-white! [&_h1]:text-white! [&_h2]:text-white! [&_h3]:text-white! [&_h4]:text-white! [&_h5]:text-white! [&_h6]:text-white! [&_li]:text-white! [&_p]:font-sans! [&_p]:text-white!"
                    >
                        <div className={CONTENT_CAP_PADDED}>
                            <DetailModules
                                isMobile={isMobile}
                                modules={modulesWithAds}
                                isProjectRevamp
                                // The module list is a single-column grid with an
                                // implicit `auto` track, so one module wider than the
                                // column (a full-bleed block in some galleries) sizes
                                // the track to its max-content and drags every module
                                // — including the centred full-page images — right.
                                // Pin the track to the container so a wide module can
                                // overflow on its own without shifting the rest.
                                // Figma spaces the modules 3.12rem (50px) apart on
                                // desktop, overriding the renderer's default gap-12;
                                // captioned modules also zero their legacy 125px
                                // caption bottom margin (DetailSingleImageShell.style)
                                // so this gap is the whole inter-module space.
                                className="grid-cols-[minmax(0,1fr)] md:gap-[3.12rem]"
                            />
                        </div>
                    </section>
                </ModulesContextProvider>
            </ProductTaggingContextProvider>
        </PaintPickerContextProvider>
    );
};
